import type React from "react";
import { Css, type CssProps } from "../css";

export namespace JSX {
  export type ElementType = string | React.JSXElementConstructor<any>;

  export interface Element extends React.JSX.Element {}

  export interface ElementClass extends React.JSX.ElementClass {}

  export interface ElementAttributesProperty
    extends React.JSX.ElementAttributesProperty {}

  export interface ElementChildrenAttribute
    extends React.JSX.ElementChildrenAttribute {}

  export type LibraryManagedAttributes<C, P> =
    React.JSX.LibraryManagedAttributes<C, P>;

  export interface IntrinsicAttributes extends React.JSX.IntrinsicAttributes {}

  export interface IntrinsicClassAttributes<T>
    extends React.JSX.IntrinsicClassAttributes<T> {}

  export type IntrinsicElements = {
    [K in keyof React.JSX.IntrinsicElements]: Omit<
      React.JSX.IntrinsicElements[K],
      "className"
    > &
      CssProps;
  };
}

export const wrap = (
  jsx: (
    type: React.ElementType,
    config: unknown,
    maybeKey: React.Key | undefined,
    ...rest: any[]
  ) => unknown,
) => {
  return (
    type: React.ElementType,
    config:
      | ({
          key: React.Key | undefined;
          children: React.ReactNode;
        } & CssProps)
      | undefined,
    maybeKey: React.Key,
    ...rest: unknown[]
  ) => {
    if (
      typeof type !== "string" ||
      !config ||
      !(
        config.css ||
        config.__experimental_placeholderCss ||
        config.__experimental_webkitScrollbarCss ||
        config.__experimental_webkitScrollbarCornerCss ||
        config.__experimental_webkitScrollbarButtonCss ||
        config.__experimental_webkitScrollbarThumbCss ||
        config.__experimental_webkitScrollbarThumbHoverCss ||
        config.__experimental_webkitSearchCancelButtonCss ||
        config.__experimental_webkitSearchDecorationCss ||
        config.__experimental_beforeCss ||
        config.__experimental_afterCss
      )
    ) {
      return jsx(type, config, maybeKey, ...rest);
    }

    const {
      css,
      __experimental_placeholderCss,
      __experimental_webkitScrollbarCss,
      __experimental_webkitScrollbarCornerCss,
      __experimental_webkitScrollbarButtonCss,
      __experimental_webkitScrollbarThumbCss,
      __experimental_webkitScrollbarThumbHoverCss,
      __experimental_webkitSearchCancelButtonCss,
      __experimental_webkitSearchDecorationCss,
      __experimental_beforeCss,
      __experimental_afterCss,
      children,
      ...props
    } = config;

    return jsx(
      Css,
      {
        css,
        __experimental_placeholderCss,
        __experimental_webkitScrollbarCss,
        __experimental_webkitScrollbarCornerCss,
        __experimental_webkitScrollbarButtonCss,
        __experimental_webkitScrollbarThumbCss,
        __experimental_webkitScrollbarThumbHoverCss,
        __experimental_webkitSearchCancelButtonCss,
        __experimental_webkitSearchDecorationCss,
        __experimental_beforeCss,
        __experimental_afterCss,
        type,
        props,
        children,
      },
      maybeKey,
      ...rest,
    );
  };
};
